// Guias de obtenção de credenciais para corretoras e provedores de IA.
// Os passos descrevem conceitos estáveis (menus principais), evitando texto exato de botões
// que podem mudar a cada redesign. Sempre há link para a documentação oficial como fallback.

const LAST_VERIFIED = '2026-05-28';

/**
 * Boas práticas de segurança válidas pra TODAS as corretoras. Renderizado
 * uma vez por tela (não por exchange) — evita repetir o mesmo texto em
 * cada guide.
 */
export const SHARED_EXCHANGE_SECURITY = {
    title: 'Boas práticas de segurança (todas as corretoras)',
    intro: 'Configure desta forma pra isolar o bot do seu capital principal e cobrir múltiplos cenários de falha (vazamento de chave, bug do bot, conta principal comprometida).',
    tips: [
        {
            label: '1. Use uma sub-account dedicada',
            detail: 'Cada uma das três suportadas (Binance, OKX, Bybit, Bitget) permite criar sub-accounts. Coloca só o capital que você está disposto a perder na sub-account. Se o bot fizer trade ruim ou a key vazar, o estrago é limitado àquele saldo — sua conta principal fica intocada. Veja os passos específicos por corretora abaixo, no guia da exchange selecionada.'
        },
        {
            label: '2. API key com permissão TRADE apenas',
            detail: 'Em todas as exchanges, ao criar a API key marque apenas Read + Spot Trade (e Margin/Futures se for operar). NUNCA habilite Withdraw. Mesmo se a key vazar, atacante não consegue sacar fundos.'
        },
        {
            label: '3. IP whitelist da API key',
            detail: 'Configure a key pra aceitar requests só do IP público da sua VM. Descobre rodando "curl ifconfig.me" no terminal da VM. Camada extra de defesa — mesmo com a key + secret em mãos, atacante de outro IP não autentica.'
        },
        {
            label: '4. Capital reduzido na conta operada',
            detail: 'Mantenha o mínimo viável de capital na sub-account onde o bot opera. Resto fica em savings, staking ou outra wallet que a API do bot não vê. Se quiser escalar, AUMENTA o saldo da sub-account — não migra pra main.'
        },
        {
            label: '5. Nunca compartilhe a key entre instâncias',
            detail: 'Uma API key por instância do Hydra. Se você rodar Hydra em dev local + Oracle produção, cria 2 keys diferentes (com IPs diferentes no whitelist). Evita race conditions e facilita identificar origem em logs de auditoria.'
        },
        {
            label: '6. Backup das credenciais fora do banco',
            detail: 'Tem que armazenar Access Key + Secret + Passphrase (quando aplicável) num cofre próprio (1Password, Bitwarden, KeePass). O Hydra criptografa no banco, mas se você perder o banco/AES_KEY, precisa recadastrar. Sem backup externo, vai ter que regerar tudo na exchange.'
        }
    ]
};

export const EXCHANGE_GUIDES = {
    BINANCE: {
        providerName: 'Binance',
        intro: 'A Binance gera um par Access Key / Secret Key. A Secret Key é exibida uma única vez no momento da criação — guarde-a com cuidado.',
        steps: [
            'Faça login em <strong>binance.com</strong> e acesse <strong>Perfil → Gerenciamento de API</strong> (ou <em>API Management</em>).',
            'Clique em <strong>Criar API</strong>, escolha o tipo <strong>System generated</strong>, defina um rótulo (ex: "Hydra") e confirme com 2FA / e-mail / SMS.',
            'Copie a <strong>API Key</strong> (Access Key) e a <strong>Secret Key</strong> imediatamente — a Secret só é exibida uma vez.',
            'Em <strong>Editar restrições</strong>, marque apenas <strong>Enable Spot &amp; Margin Trading</strong>. <em>Não</em> habilite saques.',
            '(Recomendado) Restrinja o acesso ao seu IP fixo em <strong>Restrict access to trusted IPs only</strong>.',
            'Cole a Access Key e a Secret Key nos campos correspondentes do Hydra e clique em <strong>Salvar</strong>.'
        ],
        warnings: [
            'NUNCA habilite a permissão de saque na API key — o Hydra não precisa dela.',
            'Se você perder a Secret Key, terá que apagar essa key e criar outra.'
        ],
        testnet: {
            intro: 'A Binance mantém um testnet separado em testnet.binance.vision com saldo virtual. As credenciais são completamente independentes da sua conta de produção.',
            steps: [
                'Acesse <strong>testnet.binance.vision</strong> e clique em <strong>Log In with GitHub</strong> (obrigatório — só autenticação via GitHub).',
                'Após login, vá em <strong>API Keys</strong> e clique em <strong>Generate HMAC_SHA256 Key</strong> (não use Ed25519 — o Hydra usa HMAC).',
                'Copie a <strong>API Key</strong> e a <strong>Secret Key</strong> geradas.',
                'Para obter saldo virtual, clique em <strong>Faucet</strong> no menu e solicite BTC e USDT de teste.',
                'No Hydra, cole essas credenciais nos campos <strong>Test Access Key</strong> e <strong>Test Secret Key</strong>.'
            ],
            note: 'O testnet reseta periodicamente. Se as ordens começarem a falhar sem motivo, verifique se o saldo foi zerado e use o Faucet novamente.'
        },
        futuresTestnet: {
            intro: 'Atenção: o testnet de FUTUROS é separado do spot. Fica em testnet.binancefuture.com — site e credenciais PRÓPRIOS. As chaves do spot testnet (testnet.binance.vision) NÃO funcionam em futuros.',
            steps: [
                'Acesse <strong>testnet.binancefuture.com</strong> e entre (dá pra logar com a conta GitHub ou criar um cadastro de teste).',
                'Na parte inferior da tela, abra a aba <strong>API Key</strong> e copie a <strong>API Key</strong> e a <strong>API Secret</strong> (HMAC) geradas para a sua conta de teste.',
                'A carteira de futuros (USDT-M) do testnet já vem com saldo virtual — use-o, ou o faucet do próprio testnet se disponível.',
                'Guarde essas credenciais: elas são usadas na configuração de <strong>futuros</strong> do Hydra, em campos separados das chaves de spot.'
            ],
            note: 'As chaves de futuros são independentes das de spot — cada ambiente (spot/futuros, produção/testnet) tem o seu próprio par. O testnet de futuros também reseta periodicamente.'
        },
        subAccount: {
            intro: 'Recomendado: opere o bot a partir de uma sub-account com saldo isolado.',
            steps: [
                'Acesse <strong>Account Center → Sub Account</strong> (ou pesquise "Sub Account" no menu).',
                'Clique em <strong>Create Sub Account</strong>, defina um e-mail virtual (ex: "hydra@seu-email") e uma senha.',
                'Após criada, faça <strong>Transfer</strong> da Main pra Sub Account com o capital que vai expor ao bot.',
                'Faça login na sub-account (ou use "Manage Sub Account") e crie a API key seguindo os passos acima.',
                'No Hydra, cole as credenciais da sub-account — não da main.'
            ],
            note: 'Sub-accounts em contas brasileiras pode ter restrições KYC. Se não aparecer no menu, use a main com permissões restritas + IP whitelist.'
        },
        docUrl: 'https://www.binance.com/en/support/faq/360002502072',
        lastVerified: LAST_VERIFIED
    },

    BYBIT: {
        providerName: 'Bybit',
        intro: 'A Bybit usa contas unificadas (UNIFIED). O Hydra opera spot e perpétuos via esse tipo de conta.',
        steps: [
            'Faça login em <strong>bybit.com</strong> e acesse <strong>Ícone de perfil → API</strong>.',
            'Clique em <strong>Create New Key</strong> e escolha <strong>System-generated API Keys</strong>.',
            'Selecione o tipo <strong>API Transaction</strong> e marque as permissões: <strong>Unified Trading → Trade</strong>, <strong>SPOT → Trade</strong> e <strong>Wallet → Read</strong>.',
            'Defina expiração (opcional) e — recomendado — restrinja por IP.',
            'Confirme com 2FA. Copie a <strong>API Key</strong> e a <strong>API Secret</strong> imediatamente (a secret só é exibida uma vez).',
            'Cole no Hydra e clique em <strong>Salvar</strong>.'
        ],
        warnings: [
            'Use uma conta UNIFIED (não Standard Account). O Hydra assume esse tipo.',
            'Não habilite Withdrawal nas permissões.'
        ],
        testnet: {
            intro: 'A Bybit tem um testnet separado (testnet.bybit.com) com saldo virtual e ambiente UNIFIED idêntico ao de produção.',
            steps: [
                'Acesse <strong>testnet.bybit.com</strong> e crie uma conta independente (e-mail + senha — não precisa ser o mesmo da produção).',
                'Após login, vá em <strong>Account & Security → API Management</strong> e clique em <strong>Create New Key</strong>.',
                'Escolha <strong>API Transaction</strong>, marque as mesmas permissões de produção (Unified Trading → Trade, Spot → Trade, Wallet → Read).',
                'Copie a <strong>API Key</strong> e a <strong>API Secret</strong>.',
                'Para obter saldo virtual, procure a opção de <strong>Deposit / Faucet</strong> no testnet e solicite USDT de teste.',
                'No Hydra, cole essas credenciais nos campos <strong>Test Access Key</strong> e <strong>Test Secret Key</strong>.'
            ],
            note: 'O testnet da Bybit é um ambiente separado — orders feitas lá não afetam sua conta de produção.'
        },
        subAccount: {
            intro: 'Recomendado: crie uma sub-account UNIFIED só pro bot.',
            steps: [
                'Acesse <strong>Account & Security → Sub Account</strong>.',
                'Clique em <strong>Create Sub Account</strong>, defina um username e senha.',
                'Defina o tipo como <strong>Unified Trading Account</strong> (mesmo tipo do Hydra).',
                'Faça <strong>Internal Transfer</strong> da main pra sub-account com o capital inicial.',
                'Crie a API key dentro da sub-account seguindo os passos acima.'
            ]
        },
        docUrl: 'https://www.bybit.com/app/user/api-management',
        lastVerified: LAST_VERIFIED
    },

    OKX: {
        providerName: 'OKX',
        intro: 'A OKX exige três credenciais: API Key, Secret Key e <strong>Passphrase</strong> (que você define no momento da criação).',
        steps: [
            'Faça login em <strong>okx.com</strong> e acesse <strong>Perfil → API</strong> (ou <em>Trade API</em>).',
            'Clique em <strong>Create V5 API Key</strong>.',
            'Defina um nome (ex: "Hydra") e — IMPORTANTE — escolha uma <strong>Passphrase</strong> própria. Anote-a, pois você precisará digitar no Hydra e a OKX não exibe novamente.',
            'Permissões: marque <strong>Read</strong> e <strong>Trade</strong>. Deixe <strong>Withdraw</strong> desmarcado.',
            '(Recomendado) Restrinja por IP em <strong>IP Address</strong>.',
            'Confirme com 2FA. Copie <strong>API Key</strong>, <strong>Secret Key</strong> e use a Passphrase que você definiu.',
            'Cole as três credenciais nos campos correspondentes do Hydra e clique em <strong>Salvar</strong>.'
        ],
        warnings: [
            'A Passphrase é definida POR VOCÊ no momento da criação — não é gerada pela OKX.',
            'Se você esquecer a Passphrase, precisará apagar a key e criar outra.'
        ],
        testnet: {
            intro: 'A OKX usa <strong>Demo Trading</strong> dentro da própria conta — sem site separado. O Hydra adiciona o header <code>x-simulated-trading: 1</code> automaticamente ao usar o ambiente de teste.',
            steps: [
                'Faça login em <strong>okx.com</strong> e alterne para o modo <strong>Demo Trading</strong> (geralmente um toggle no menu do perfil ou na plataforma de trading).',
                'Ainda em modo Demo, acesse <strong>Perfil → API</strong> e crie uma nova API Key V5 seguindo os mesmos passos de produção.',
                '<strong>Defina uma Passphrase exclusiva para esta key de demo</strong> — pode ser diferente da produção.',
                'Copie a <strong>API Key</strong>, <strong>Secret Key</strong> e anote a <strong>Passphrase</strong>.',
                'O saldo demo é pré-carregado (~$100.000 USDT virtual) — não precisa de faucet.',
                'No Hydra, cole as credenciais nos campos <strong>Test Access Key</strong>, <strong>Test Secret Key</strong> e <strong>Test Passphrase</strong>.'
            ],
            note: 'As credenciais de demo da OKX só funcionam em modo demo. O Hydra identifica isso pelo ambiente da automação — não mistura com produção.'
        },
        subAccount: {
            intro: 'Recomendado: opere o bot a partir de uma sub-account.',
            steps: [
                'Acesse <strong>Personal Center → Sub-account</strong>.',
                'Clique em <strong>Create Sub-account</strong>, defina um nome e senha.',
                'Escolha o tipo de conta (Trading ou Custody — para Hydra use Trading).',
                'Faça <strong>Internal Transfer</strong> da main pra sub-account.',
                'Crie a API V5 dentro da sub-account seguindo os passos acima. <strong>Cada sub-account tem sua própria Passphrase</strong>.'
            ]
        },
        docUrl: 'https://www.okx.com/docs-v5/en/#overview-api-resources-and-support',
        lastVerified: LAST_VERIFIED
    },

    BITGET: {
        providerName: 'Bitget',
        intro: 'A Bitget também exige Passphrase além de Key e Secret. A Passphrase é definida por você.',
        steps: [
            'Faça login em <strong>bitget.com</strong> e acesse <strong>Perfil → Gerenciamento de API</strong> (<em>API Management</em>).',
            'Clique em <strong>Create API</strong> e escolha <strong>System-generated</strong>.',
            'Defina um rótulo e uma <strong>Passphrase</strong> própria — anote-a, será exigida no Hydra.',
            'Permissões: marque <strong>Read-only</strong> e <strong>Spot Trade</strong> (e <strong>Contract Trade</strong> se for operar futuros). Não marque <strong>Transfer</strong> ou <strong>Withdraw</strong>.',
            '(Recomendado) Restrinja por IP.',
            'Confirme com 2FA. Copie <strong>API Key</strong>, <strong>Secret Key</strong>.',
            'Cole as três credenciais (Key, Secret, Passphrase) no Hydra e clique em <strong>Salvar</strong>.'
        ],
        warnings: [
            'Igual à OKX: a Passphrase é DEFINIDA por você, não gerada automaticamente.',
            'Sem permissão de saque/transferência habilitada.'
        ],
        testnet: {
            intro: 'A Bitget oferece <strong>Simulated Trading</strong> (trading simulado) com endpoint separado (<code>api-simulated.bitget.com</code>). O Hydra troca para esse endpoint automaticamente ao usar o ambiente de teste.',
            steps: [
                'Faça login em <strong>bitget.com</strong> e acesse a seção de <strong>Simulated Trading</strong> (geralmente em "Trade → Simulated Trading" ou no menu do perfil).',
                'Dentro do modo simulado, acesse <strong>API Management</strong> e crie uma API Key seguindo os mesmos passos de produção.',
                '<strong>Defina uma Passphrase exclusiva</strong> para esta key simulada.',
                'Copie a <strong>API Key</strong>, <strong>Secret Key</strong> e anote a <strong>Passphrase</strong>.',
                'O saldo simulado é virtual — a Bitget oferece fundos de teste ao ativar o modo.',
                'No Hydra, cole as credenciais nos campos <strong>Test Access Key</strong>, <strong>Test Secret Key</strong> e <strong>Test Passphrase</strong>.'
            ],
            note: 'Se a opção de Simulated Trading não aparecer, verifique se sua conta passou pelo KYC básico — algumas regiões exigem verificação para acessar esse modo.'
        },
        subAccount: {
            intro: 'Recomendado: crie uma sub-account dedicada ao bot.',
            steps: [
                'Acesse <strong>Profile → Sub-Account Management</strong>.',
                'Clique em <strong>Create Sub-Account</strong> e defina nome/senha.',
                'Faça <strong>Asset Transfer</strong> da main pra sub-account.',
                'Crie a API key dentro da sub-account seguindo os passos acima. <strong>Defina nova Passphrase</strong> independente da main.'
            ]
        },
        docUrl: 'https://www.bitget.com/api-doc/common/intro',
        lastVerified: LAST_VERIFIED
    }
};

export const AI_GUIDES = {
    GROQ: {
        providerName: 'Groq',
        intro: 'Groq tem plano grátis generoso (sem necessidade de cartão de crédito). Ideal para começar.',
        steps: [
            'Acesse <strong>console.groq.com</strong> e crie uma conta (Google, GitHub ou e-mail).',
            'Após login, vá em <strong>API Keys</strong> no menu lateral.',
            'Clique em <strong>Create API Key</strong>, dê um nome (ex: "Hydra") e copie a chave gerada (começa com <code>gsk_</code>).',
            'Cole no campo <strong>API Key da IA</strong> do Hydra e salve.'
        ],
        warnings: [
            'Plano grátis tem rate limits por minuto. Se atingir, espere 1 minuto ou faça upgrade.'
        ],
        docUrl: 'https://console.groq.com/docs/quickstart',
        lastVerified: LAST_VERIFIED
    },

    CEREBRAS: {
        providerName: 'Cerebras',
        intro: 'Cerebras tem plano grátis generoso (1M tokens/dia, sem cartão) e inferência ultrarrápida. Ótimo para o modo Distribuído (sharding).',
        steps: [
            'Acesse <strong>cloud.cerebras.ai</strong> e crie uma conta (Google ou e-mail).',
            'Após login, vá em <strong>API Keys</strong>.',
            'Clique em <strong>Create API Key</strong>, dê um nome (ex: "Hydra") e copie a chave gerada (começa com <code>csk-</code>).',
            'Cole no campo <strong>API Key da IA</strong> do Hydra e salve.'
        ],
        warnings: [
            'Plano grátis tem rate limits por minuto. O modelo padrão (gpt-oss-120b) é de raciocínio — já configuramos esforço baixo para caber no orçamento do sinal.'
        ],
        docUrl: 'https://inference-docs.cerebras.ai/',
        lastVerified: LAST_VERIFIED
    },

    OPENROUTER: {
        providerName: 'OpenRouter',
        intro: 'Com UMA chave do OpenRouter você acessa dezenas de modelos com tier grátis (DeepSeek, Llama, etc). Ótima raia extra para o modo Distribuído (sharding).',
        steps: [
            'Acesse <strong>openrouter.ai</strong> e crie uma conta (Google, GitHub ou e-mail).',
            'Vá em <strong>Keys</strong> (openrouter.ai/keys) e clique em <strong>Create Key</strong>.',
            'Copie a chave gerada (começa com <code>sk-or-v1-</code>) e cole no campo <strong>API Key da IA</strong> do Hydra.',
            'Escolha um modelo terminado em <code>:free</code> (ex: GPT-OSS 120B free). O padrão já é um modelo grátis; se ele sumir do catálogo, use "↻ Atualizar da API" pra ver os <code>:free</code> atuais.'
        ],
        warnings: [
            'Para usar os modelos <code>:free</code>, ative em <strong>Settings → Privacy</strong> a opção de permitir treino com prompts (exigida pelo OpenRouter para liberar o tier grátis).',
            'Free tier limita por requisições (~20/min, ~200/dia), não por tokens. Ao atingir, o sharding cai no próximo provedor.'
        ],
        docUrl: 'https://openrouter.ai/docs/quickstart',
        lastVerified: LAST_VERIFIED
    },

    SAMBANOVA: {
        providerName: 'SambaNova Cloud',
        intro: 'SambaNova tem plano grátis (sem cartão) e inferência muito rápida sobre modelos open-source (Llama 3.3 70B). Boa raia para o sharding.',
        steps: [
            'Acesse <strong>cloud.sambanova.ai</strong> e crie uma conta (Google ou e-mail).',
            'Vá em <strong>APIs</strong> / <strong>API Keys</strong> no painel.',
            'Clique em <strong>Generate New Key</strong> e copie a chave.',
            'Cole no campo <strong>API Key da IA</strong> do Hydra e salve. O modelo padrão é Llama 3.3 70B.'
        ],
        warnings: [
            'Plano grátis tem rate limits por minuto. Se atingir, o sharding cai no próximo provedor.'
        ],
        docUrl: 'https://docs.sambanova.ai/',
        lastVerified: LAST_VERIFIED
    },

    ZHIPU: {
        providerName: 'Zhipu GLM (z.ai)',
        intro: 'O GLM-4.5-Flash da Zhipu AI é genuinamente grátis e tem contexto enorme. Mais uma raia para o modo Distribuído (sharding).',
        steps: [
            'Acesse <strong>z.ai</strong> (ou open.bigmodel.cn) e crie uma conta.',
            'Vá em <strong>API Keys</strong> no painel de desenvolvedor e gere uma chave.',
            'Copie a chave e cole no campo <strong>API Key da IA</strong> do Hydra.',
            'O modelo padrão (GLM-4.5-Flash) é grátis. Já configuramos o raciocínio desligado para caber no orçamento do sinal.'
        ],
        warnings: [
            'Modelos GLM são de raciocínio híbrido — o Hydra desliga o "thinking" automaticamente para a saída curta do sinal não estourar tokens.'
        ],
        docUrl: 'https://docs.z.ai/',
        lastVerified: LAST_VERIFIED
    },

    GITHUB: {
        providerName: 'GitHub Models',
        intro: 'O GitHub Models dá acesso grátis (limite por requisições) a um catálogo grande de modelos (GPT-4o-mini, Llama, Mistral...). A "API key" é um GitHub PAT. Mais uma raia para o modo Distribuído (sharding).',
        steps: [
            'Acesse <strong>github.com/settings/personal-access-tokens</strong> e gere um <strong>Fine-grained token</strong>.',
            'Em permissões, habilite <strong>Models: Read-only</strong> (escopo <code>models:read</code>). Não precisa de acesso a repositórios.',
            'Copie o token (começa com <code>github_pat_</code>) e cole no campo <strong>API Key da IA</strong> do Hydra.',
            'O modelo padrão (openai/gpt-4o-mini) é grátis e suporta saída em JSON.'
        ],
        warnings: [
            'O free tier do GitHub Models é limitado por REQUISIÇÕES (rate-limit), não por tokens. Em alta cadência pode bater o limite — o fallback do sharding cobre.',
            'Os IDs de modelo vêm no formato editor/modelo (ex.: openai/gpt-4o-mini, meta/Llama-3.3-70B-Instruct).'
        ],
        docUrl: 'https://docs.github.com/en/github-models',
        lastVerified: LAST_VERIFIED
    },

    GEMINI: {
        providerName: 'Gemini (Google AI Studio)',
        intro: 'O Gemini Flash tem plano grátis: 15 requisições/minuto e ~1M tokens/dia. Suficiente para uso pessoal moderado.',
        steps: [
            'Acesse <strong>aistudio.google.com</strong> e faça login com sua conta Google.',
            'No canto superior, clique em <strong>Get API key</strong> (ou ícone de chave).',
            'Clique em <strong>Create API key in new project</strong> (ou selecione um projeto existente).',
            'Copie a chave gerada.',
            'Cole no campo <strong>API Key da IA</strong> do Hydra e salve.'
        ],
        warnings: [
            'A região onde o Gemini é gratuito pode mudar — consulte a documentação se houver erro de "API not available".',
            'No Hydra, use o modelo <code>gemini-3.1-flash-lite</code> — é o Flash-Lite atual da chave grátis (rápido e dentro da cota).'
        ],
        docUrl: 'https://ai.google.dev/gemini-api/docs/api-key',
        lastVerified: LAST_VERIFIED
    },

    DEEPSEEK: {
        providerName: 'DeepSeek',
        intro: 'DeepSeek é pago, mas com custo extremamente baixo (~10x mais barato que GPT-4o). Recarga mínima costuma ser ~$2.',
        steps: [
            'Acesse <strong>platform.deepseek.com</strong> e crie uma conta.',
            'Vá em <strong>Billing</strong> e adicione créditos (pagamento via cartão internacional ou similar).',
            'Vá em <strong>API Keys</strong> e clique em <strong>Create new API key</strong>.',
            'Dê um nome (ex: "Hydra") e copie a chave gerada (começa com <code>sk-</code>).',
            'Cole no campo <strong>API Key da IA</strong> do Hydra e salve.'
        ],
        warnings: [
            'Sem créditos, todas as chamadas falham com erro 402.',
            'Use o modelo <code>deepseek-chat</code> para custo mínimo; <code>deepseek-reasoner</code> é mais caro e mais lento.'
        ],
        docUrl: 'https://api-docs.deepseek.com/',
        lastVerified: LAST_VERIFIED
    },

    CLAUDE: {
        providerName: 'Claude (Anthropic)',
        intro: 'Claude é pago e premium — tem o melhor raciocínio nuançado e cache de prompt nativo (reduz custo em chamadas repetidas).',
        steps: [
            'Acesse <strong>console.anthropic.com</strong> e crie uma conta.',
            'Confirme e-mail e adicione método de pagamento em <strong>Plans &amp; Billing</strong> (cartão de crédito internacional).',
            'Compre créditos iniciais (mínimo costuma ser $5).',
            'Vá em <strong>API Keys</strong> → <strong>Create Key</strong>.',
            'Dê um nome (ex: "Hydra") e copie a chave (começa com <code>sk-ant-</code>). Ela só é exibida uma vez.',
            'Cole no campo <strong>API Key da IA</strong> do Hydra e salve.'
        ],
        warnings: [
            'A chave Claude só é exibida no momento da criação — não há como recuperá-la depois.',
            'Modelos Sonnet/Haiku são bem mais baratos que Opus. Para uso contínuo no scanner, recomendamos Haiku ou Sonnet.'
        ],
        docUrl: 'https://docs.anthropic.com/en/api/getting-started',
        lastVerified: LAST_VERIFIED
    },

    MISTRAL: {
        providerName: 'Mistral',
        intro: 'Provedor europeu (França). Custo intermediário entre DeepSeek e OpenAI.',
        steps: [
            'Acesse <strong>console.mistral.ai</strong> e crie uma conta.',
            'Vá em <strong>Workspace → Billing</strong> e adicione método de pagamento.',
            'Vá em <strong>API Keys</strong> → <strong>Create new key</strong>.',
            'Defina um nome e (opcional) data de expiração.',
            'Copie a chave gerada.',
            'Cole no campo <strong>API Key da IA</strong> do Hydra e salve.'
        ],
        warnings: [
            'A Mistral tem um plano "La Plateforme" gratuito para avaliação, mas com limites bem baixos. Para uso real, ative o plano pago.'
        ],
        docUrl: 'https://docs.mistral.ai/getting-started/quickstart/',
        lastVerified: LAST_VERIFIED
    },

    OPENAI: {
        providerName: 'OpenAI',
        intro: 'OpenAI (ChatGPT API) é pago e premium. Para análise técnica de cripto, DeepSeek/Groq custam menos com qualidade similar.',
        steps: [
            'Acesse <strong>platform.openai.com</strong> e crie uma conta (separada da conta ChatGPT consumer).',
            'Adicione método de pagamento em <strong>Settings → Billing → Payment methods</strong>.',
            'Compre créditos iniciais ou ative auto-recarga (mínimo $5).',
            'Vá em <strong>API keys</strong> → <strong>Create new secret key</strong>.',
            'Dê um nome (ex: "Hydra") e copie a chave (começa com <code>sk-</code>). Ela só é exibida uma vez.',
            'Cole no campo <strong>API Key da IA</strong> do Hydra e salve.'
        ],
        warnings: [
            'Conta ChatGPT (chat.openai.com) e conta de API (platform.openai.com) são separadas. O plano ChatGPT Plus NÃO inclui créditos de API.',
            'A chave secreta só aparece uma vez — copie e salve em local seguro.',
            'Para custo menor, use <code>gpt-4o-mini</code> em vez de <code>gpt-4o</code>.'
        ],
        docUrl: 'https://platform.openai.com/docs/quickstart',
        lastVerified: LAST_VERIFIED
    },

    GROK: {
        providerName: 'Grok (xAI)',
        intro: 'Grok é o provedor mais caro desta lista. Use apenas se já tem assinatura X/Premium ou interesse específico nele.',
        steps: [
            'Acesse <strong>console.x.ai</strong> e faça login com sua conta X (Twitter).',
            'Adicione método de pagamento em <strong>Billing</strong>.',
            'Vá em <strong>API Keys</strong> → <strong>Create API Key</strong>.',
            'Dê um nome e copie a chave gerada (começa com <code>xai-</code>).',
            'Cole no campo <strong>API Key da IA</strong> do Hydra e salve.'
        ],
        warnings: [
            'Custo bem maior que DeepSeek/Groq para a mesma análise.',
            'Algumas regiões podem ter restrição de acesso ao xAI.'
        ],
        docUrl: 'https://docs.x.ai/docs',
        lastVerified: LAST_VERIFIED
    }
};

const setupGuides = { EXCHANGE_GUIDES, AI_GUIDES, SHARED_EXCHANGE_SECURITY };
export default setupGuides;
